// Pure formatters: turn the loose study-inputs payload into display-ready rows
// for the four rail cards. Kept separate from JSX so the value/provenance
// extraction is unit-testable. Every getter is defensive — a missing field
// yields no row rather than throwing (the payload is an audit bag that may grow).
import type { StudyInputs } from "../../api/studyInputs";

export type DetailRow = { label: string; value: string; source?: string | null };

function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

// A cell -> formatted string + source, or null if absent. Accepts either a
// {value, source} object (platform/spring fields) OR a plain number/string
// (tyre k_vert is stored bare, with no per-value provenance).
function cell(v: unknown, unit = "", scale = 1): { value: string; source: string | null } | null {
  if (typeof v === "number" || typeof v === "string") {
    v = { value: v, source: null };
  }
  const o = asObj(v);
  if (!o || o.value == null) return null;
  const raw = o.value;
  const num = typeof raw === "number" ? raw : Number(raw);
  const value = Number.isFinite(num) && typeof raw === "number"
    ? `${(num * scale).toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit}`
    : `${raw}`;
  return { value, source: (o.source as string) ?? null };
}

const CORNERS = ["lf", "rf", "lr", "rr"] as const;

// Phase 4: the rig-actuation source is recorded as both REQUESTED
// (kpi_targets.track_replay) and EFFECTIVE (read off the production provenance).
const ACTUATION_LABEL: Record<string, string> = {
  track_replay: "track replay",
  cached_seven_post: "cached seven-post",
  none: "no recording",
};
function actuationLabel(v: unknown, isReplayFallback: boolean): string {
  if (typeof v === "string" && v in ACTUATION_LABEL) return ACTUATION_LABEL[v];
  // Back-compat: older artifacts carry only `is_replay`.
  return isReplayFallback ? "track replay" : "cached seven-post";
}

export function rigRows(inputs: StudyInputs): DetailRow[] {
  const r = asObj(inputs.rig_actuation);
  if (!r) return [];
  const rows: DetailRow[] = [];
  // Requested vs effective actuation source. Since the runner now FAILS rather
  // than falling back, these agree (✓) for any successful run; a ⚠ flags any
  // future divergence.
  const isReplay = Boolean(r.is_replay);
  const requested = actuationLabel(r.requested_source, isReplay);
  const effective = actuationLabel(r.effective_source, isReplay);
  const reconciled =
    r.reconciled == null ? requested === effective : Boolean(r.reconciled);
  rows.push({ label: "requested", value: requested });
  rows.push({
    label: "effective",
    value: reconciled
      ? `${effective} ✓`
      : `${effective} ⚠ (requested ${requested})`,
    source: (r.effective_source_detail as string) ?? null,
  });
  if (r.recording) rows.push({ label: "recording", value: String(r.recording) });
  if (r.duration_s != null) rows.push({ label: "duration", value: `${r.duration_s}s` });
  if (r.sample_rate_hz != null) rows.push({ label: "sample rate", value: `${r.sample_rate_hz} Hz` });
  if (r.n_channels != null) rows.push({ label: "channels", value: String(r.n_channels) });
  if (r.body_post_convention) rows.push({ label: "body-post", value: String(r.body_post_convention) });
  return rows;
}

// kN/m reads better than N/m for tyre/spring stiffness.
function cornerStiffnessRow(label: string, group: unknown): DetailRow[] {
  const g = asObj(group);
  if (!g) return [];
  const parts: string[] = [];
  let source: string | null = null;
  for (const c of CORNERS) {
    const cl = cell(g[c], "", 0.001); // N/m -> kN/m
    if (cl) { parts.push(`${c.toUpperCase()} ${cl.value}`); source = cl.source; }
  }
  return parts.length ? [{ label, value: parts.join(" · ") + " kN/m", source }] : [];
}

export function platformRows(inputs: StudyInputs): DetailRow[] {
  const p = asObj(inputs.car_platform);
  if (!p) return [];
  const rows: DetailRow[] = [];
  rows.push(...cornerStiffnessRow("springs", p.spring_rate_N_per_m));
  const arb = asObj(p.arb_Nm_per_rad);
  if (arb) {
    const f = cell(arb.front), r = cell(arb.rear);
    if (f || r) rows.push({
      label: "ARB f/r",
      value: `${f?.value ?? "—"} / ${r?.value ?? "—"} Nm/rad`,
      source: (f ?? r)?.source ?? null,
    });
  }
  const cw = asObj(p.corner_weight_lb);
  if (cw) {
    const parts = CORNERS.map((c) => cell(cw[c])).filter(Boolean) as { value: string; source: string | null }[];
    if (parts.length === 4) rows.push({
      label: "corner wt",
      value: CORNERS.map((c, i) => `${c.toUpperCase()} ${parts[i].value}`).join(" · ") + " lb",
      source: parts[0].source,
    });
  }
  const mr = asObj(p.motion_ratio);
  if (mr) {
    const f = cell(mr.front), r = cell(mr.rear);
    if (f || r) rows.push({
      label: "motion ratio f/r",
      value: `${f?.value ?? "—"} / ${r?.value ?? "—"}`,
      source: (f ?? r)?.source ?? null,
    });
  }
  return rows;
}

export function tyreRows(inputs: StudyInputs): DetailRow[] {
  const t = asObj(inputs.tyre);
  if (!t) return [];
  return cornerStiffnessRow("k_vert", t.k_vert_N_per_m);
}

export function damperRows(inputs: StudyInputs): DetailRow[] {
  const d = asObj(inputs.damper);
  const pc = asObj(d?.per_corner);
  if (!pc) return [];
  const rows: DetailRow[] = [];
  for (const c of CORNERS) {
    const e = asObj(pc[c]);
    if (!e) continue;
    const bits: string[] = [];
    if (e.code) bits.push(String(e.code));
    if (e.clicks) bits.push(String(e.clicks));
    if (bits.length) rows.push({
      label: c.toUpperCase(), value: bits.join(" · "),
      source: (e.pvp_source as string) ?? null,
    });
  }
  return rows;
}
